import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table, Input, Modal, DatePicker, Typography, Row, Col, Select, Form, Button, Popconfirm, Dropdown, Menu,
} from 'antd';
import { PropTypes } from 'prop-types';
import { CalendarOutlined, MoreOutlined, FilePdfOutlined } from '@ant-design/icons';
import InfoView from 'components/common/InfoView';
import { jsPDF as JsPDF } from 'jspdf';
import 'jspdf-autotable';
import { capitalize } from 'lodash';
import moment from 'moment';
import logo from '../../assets/images/kitegateway_icon_dark.png';
import { getProcessors } from '../../redux/actions/Processors';
import {
  downloadTransactions,
  getTransactions,
  createChargeBack,
  updateRefundStatus,
  updateSettlementStatus,
  updateChargebackStatus,
  downloadSummary,
  checkTransactionStatus,
  triggerWebhook,
} from '../../redux/actions/Transactions';
import { createRefund } from '../../redux/actions/Collections';
import PageLimitSwitch from '../../components/PageLimitSwitch';
import { transactionColumns, searchableFields } from './Columns';
import DownloadBtns from '../../components/common/downloadbtns';
import TransactionDetailsForModal from './TransactionDetailsForModal';
import RefundUpdateForm from './TransactionRefundUpdateForm';
import TransactionSettlementForm from './TransactionSettlementUpdateForm';
import TransactionChargebackForm from './TransactionChargebackUpdateForm';
import { TransactionTypes, TransactionStatuses, MidBanks } from '../../constants/System';
import { TRANSACTION_CHARGEBACKS } from '../../constants/resources';
import Can from '../../util/can';
import TransactionCreateRefundForm from './TransactionCreateRefundForm';
import { SHOW_ERROR_MESSAGE } from '../../constants/ActionTypes';
import { dateAndTimeRangeQueriesArray, dateTimeRangeCondition } from '../../util/lib';
import DateRange from '../../containers/DateRange';
import GeneratePRN from '../../containers/GeneratePRN';
import InvoiceReceiptEditModal from './InvoiceReceiptEditModal';

const Transactions = ({ merchantId, transactionType, transactionStatus }) => {
  const dispatch = useDispatch();
  const { timezone } = useSelector(({ settings }) => settings);
  const { authUser } = useSelector(({ auth }) => auth);
  const isMerchant = authUser.role.name.includes('Merchant');
  const userRole = authUser?.role?.name;
  const canFilterByMidBank = userRole === 'Super Admin' || userRole === 'Admin';

  const {
    data: allTransactions,
    currentPage,
    pageSize,
    totalRecords,
    loading: allTransactionsLoading,
    downloadLoading,
  } = useSelector(({ transactions }) => transactions.allTransactions);
  const {
    data: processorsData,
    loading: processorsLoading,
  } = useSelector(({ processors }) => processors.allProcessors);
  const { dateRange } = useSelector(({ date }) => date);

  const loading = allTransactionsLoading || processorsLoading;

  const [downloadType, setDownloadType] = useState(null);
  const [visibleTransactionModal, setVisibilityTransactionModal] = useState(false);
  const [visibleUpdateRefundStatusModal, setVisibleUpdateRefundStatusModal] = useState(false);
  const [visibleUpdateSettlmtStatusModal, setVisibleUpdateSettlmtStatusModal] = useState(false);
  const [visibleUpdateChargebackStatusModal, setVisibleUpdateChargebkStatusModal] = useState(false);
  const [visibleCreateRefundModal, setVisibleCreateRefundModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState({});
  const [searchParams, setSearchParams] = useState([]);
  const [searchValue, setSearchSearchValue] = useState('');
  const [downloadRefunds, setDownloadRefunds] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [transactionFilterParams, setTransactionFilterParams] = useState([]);
  const [transactionsFilter, setTransactionsFilter] = useState('');
  const [selectedTransactionType, setSelectedTransactionType] = useState('All');
  const [selectedTransactionStatus, setSelectedTransactionStatus] = useState('All');
  const [selectedMidBank, setSelectedMidBank] = useState('All');
  const [visibleEditModal, setVisibleEditModal] = useState(false);
  const [editModalDocumentType, setEditModalDocumentType] = useState('invoice');
  const [editModalTransaction, setEditModalTransaction] = useState(null);

  const formEditRefund = useRef();
  const formEditSettlement = useRef();
  const formEditChargeback = useRef();
  const formCreateRefund = useRef();

  const generateTransactionsFilter = (merchantId, transactionStatus, transactionType, midBank) => {
    const _transactionFilterParams = [];
    if (merchantId) {
      const transactionMerchantFilter = `{"merchant": "${merchantId}"}`;
      _transactionFilterParams.push(transactionMerchantFilter);
    }

    if (canFilterByMidBank && midBank) {
      _transactionFilterParams.push(JSON.stringify({ mid_bank: midBank }));
    }

    if (transactionStatus) {
      const transactionStatusFilterParams = [];
      transactionStatus.split(',').forEach((ele) => {
        if (ele.trim().length) {
          transactionStatusFilterParams.push({ transaction_status: ele.trim() });
        }
      });
      const transactionStatusFilter = `{"$or": ${JSON.stringify(transactionStatusFilterParams)}}`;
      _transactionFilterParams.push(transactionStatusFilter);
    }

    if (transactionType && transactionType.length > 3) {
      _transactionFilterParams.push(JSON.stringify({
        transaction_type: {
          $in: transactionType.split(','),
        },
      }));
    }

    let _transactionsFilter = '';
    if (_transactionFilterParams.length > 0) {
      _transactionsFilter = `"$and":[${_transactionFilterParams.join(',')}]`;
    }

    // Don't set state here - let the caller handle it
    // The state should only be set when props change, not when called from handlers

    setTransactionFilterParams(_transactionFilterParams);
    setTransactionsFilter(_transactionsFilter);

    return _transactionsFilter;
  };

  useEffect(() => {
    // Generate filters and fetch data in one useEffect
    const _transactionsFilter = generateTransactionsFilter(merchantId, transactionStatus, transactionType);
    setSearchParams('');
    setSearchSearchValue('');

    // Set initial state based on props
    if (transactionType && transactionType.length > 3) {
      setSelectedTransactionType(capitalize(`${transactionType.toLowerCase()}s`));
    } else {
      setSelectedTransactionType('All');
    }

    if (transactionStatus && transactionStatus.length > 3) {
      setSelectedTransactionStatus(capitalize(`${transactionStatus.toLowerCase()}`));
    } else {
      setSelectedTransactionStatus('All');
    }

    let query = "";
    const sort = `sort={"created_at": -1 }`;
    const paginate = `page=${currentPage}&limit=${pageSize}`;
    const rangeStr = dateRange ? `${dateTimeRangeCondition(dateRange.startDate, dateRange.endDate, timezone)}` : null;

    const filters = [];
    if (merchantId) {
      filters.push(`"merchant": "${merchantId}"`);
    }
    if (transactionFilterParams.length > 0) {
      filters.push(_transactionsFilter);
    }
    if (rangeStr) {
      filters.push(rangeStr);
    }

    if (filters.length > 0) {
      const filter = `"$and":[{${filters.join('},{')}}]`;
      query += `filter={${filter}}`;
    } else {
      query += `filter={}`;
    }

    query += `&${sort}&${paginate}`;
    dispatch(getTransactions(query));
    dispatch(getProcessors());
    // eslint-disable-next-line
  }, [dispatch, pageSize, currentPage, dateRange]);

  const handleOnChange = (pagination, _, sort) => {
    if (pagination && sort) {
      const paginate = `page=${pagination.current || currentPage}&limit=${pageSize}`;
      let sortParams = 'sort={"created_at": -1 }';

      if (Object.keys(sort).length && sort.field) {
        sortParams = `sort={"${sort.field}": ${sort.order === 'ascend' ? '1' : '-1'} }`;
      }
      let paginateAndSort = `${paginate}&${sortParams}`;

      // Regenerate filter based on current selections
      const currentType = selectedTransactionType === 'All' ? '' : selectedTransactionType.toUpperCase();
      const currentStatus = selectedTransactionStatus === 'All' ? '' : selectedTransactionStatus.toUpperCase();

      // Remove the trailing 's' if it was added for display (e.g., "Collections" -> "COLLECTION")
      const normalizedType = currentType.endsWith('S') && currentType.length > 1
        ? currentType.slice(0, -1)
        : currentType;

      const _transactionsFilter = generateTransactionsFilter(
        merchantId,
        transactionStatus || currentStatus,
        transactionType || normalizedType,
        selectedMidBank === 'All' ? '' : selectedMidBank
      );

      const filters = [];
      if (merchantId) {
        filters.push(`"merchant": "${merchantId}"`);
      }
      if (searchParams && searchParams.length > 0) {
        filters.push(`"$or": ${JSON.stringify(searchParams)}`);
      }
      if (dateRange) {
        const rangeStr = `${dateTimeRangeCondition(dateRange.startDate, dateRange.endDate, timezone)}`;
        filters.push(rangeStr);
      }
      if (transactionFilterParams.length > 0) {
        filters.push(_transactionsFilter);
      }

      if (filters.length > 0) {
        const filter = `"$and":[{${filters.join('},{')}}]`;
        paginateAndSort += `&filter={${filter}}`;
      } else {
        paginateAndSort += `&filter={}`;
      }

      dispatch(getTransactions(paginateAndSort));
    }
  };

  const viewTransactionDetails = (record) => ({
    onClick: () => {
      setSelectedTransaction(record);
      setVisibilityTransactionModal(true);
    },
  });

  const handleSearch = (_searchValue, _, _transactionFilter) => {
    const __searchValue = _searchValue.trim().replace(/([^a-zA-Z0-9])/g, '\\$1');
    setSearchSearchValue(__searchValue);
    let params = searchableFields.map((field) => ({
      [field]: { $regex: __searchValue, $options: 'i' },
    }));

    if (__searchValue.includes('KG')) {
      params = [{ kitegateway_reference: { $regex: __searchValue, $options: 'i' } }];
    } else if (__searchValue.includes('******')) {
      params = [{ account_number: { $regex: __searchValue, $options: 'i' } }];
    } else if (['COLLECTION', 'SETTLEMENT', 'REFUND', 'CHARGEBACK', 'DISBURSEMENT', 'WITHDRAWAL', 'DEPOSIT'].includes(__searchValue.toUpperCase())) {
      params = [{ transaction_type: { $regex: __searchValue.toUpperCase(), $options: 'i' } }];
    } else if (['PENDING', 'CONFIRMING', 'COMPLETED', 'CANCELLED', 'FAILED', 'PROCESSING'].includes(__searchValue.toUpperCase())) {
      params = [{ transaction_status: { $regex: __searchValue.toUpperCase(), $options: 'i' } }];
    } else if (__searchValue?.trim()?.length === 0) {
      params = [];
    }

    setSearchParams(params);
    let search = '';
    const sort = `sort={"created_at": -1 }`;
    const paginate = `page=${currentPage}&limit=${pageSize}`;

    const filters = [];
    if (merchantId) {
      filters.push(`"merchant": "${merchantId}"`);
    }
    // an explicitly passed (possibly empty) filter must win over the stale state, otherwise clearing a filter is a no-op
    const hasExplicitFilter = _transactionFilter !== undefined;
    if (hasExplicitFilter ? _transactionFilter.length > 0 : transactionFilterParams.length > 0) {
      filters.push(hasExplicitFilter ? _transactionFilter : transactionsFilter);
    }
    if (dateRange) {
      const rangeStr = `${dateTimeRangeCondition(dateRange.startDate, dateRange.endDate, timezone)}`;
      filters.push(rangeStr);
    }
    if (params && params.length > 0) {
      filters.push(`"$or": ${JSON.stringify(params)}`);
    }

    if (filters.length > 0) {
      const filter = `"$and":[{${filters.join('},{')}}]`;
      search = `filter={${filter}}&${sort}&${paginate}`;
    } else {
      search = `filter={}&${sort}&${paginate}`;
    }

    dispatch(getTransactions(search));
  };

  const handleDownload = (type = null) => {
    if (downloadLoading) {
      return;
    }
    if (!dateRange) {
      dispatch({ type: SHOW_ERROR_MESSAGE, payload: 'Select Date range' });
      return;
    }
    const newPageSize = 500000;
    const newCurrentPage = 1;
    const rangeStr = dateRange ? `${dateTimeRangeCondition(dateRange.startDate, dateRange.endDate, timezone)}` : '';
    let searchParameters = rangeStr ? [{ ...(JSON.parse(`{${rangeStr}}`)) }] : [];
    let dateRangeParams;

    const filters = [];
    if (merchantId) {
      filters.push(`"merchant": "${merchantId}"`);
    }
    if (downloadRefunds) {
      filters.push(`"transaction_type": "REFUND"`);
    }
    if (searchParameters.length > 0) {
      filters.push(rangeStr);
    }
    if (searchParams && searchParams.length > 0) {
      filters.push(`"$or": ${JSON.stringify(searchParams)}`);
    }
    if (transactionFilterParams.length > 0) {
      filters.push(transactionsFilter);
    }

    if (filters.length > 0) {
      const filter = `"$and":[{${filters.join('},{')}}]`;
      dateRangeParams = `filter={${filter}}&sort={"created_at":-1}&limit=${newPageSize}&page=${newCurrentPage}`;
    } else {
      dateRangeParams = `filter={${rangeStr}}&sort={"created_at":-1}&limit=${newPageSize}&page=${newCurrentPage}`;
    }

    dispatch(downloadTransactions(dateRangeParams, downloadType || type, isMerchant, userRole));
  };

  const handleDownloadSummary = (type = null) => {
    if (!dateRange) {
      dispatch({ type: SHOW_ERROR_MESSAGE, payload: 'Select Date range' });
      return;
    }

    let dateRangeParams = `${dateAndTimeRangeQueriesArray(dateRange.startDate, dateRange.endDate, timezone).join('&')}`;

    if(merchantId) {
      dateRangeParams += `&merchant_id=${merchantId}`;
    }

    if (transactionFilterParams.length > 0) {
      dateRangeParams += `&transaction_type={${transactionsFilter}}`;
    }

    dispatch(downloadSummary(dateRangeParams, type));
  };

  function openDownloadModal(type) {
    setDownloadRefunds(false);
    setDownloadType(type);
    handleDownload(type);
  }

  const handleModalCancelTransactionModal = () => {
    setVisibilityTransactionModal(false);
    setSelectedTransaction({});
  };

  const handleXclRefunds = () => {
    setDownloadRefunds(true);
    setDownloadType('excel');
  };

  const changeDateRange = () => {
    setDownloadRefunds(false);
  };

  const chargeBack = (record) => {
    dispatch(createChargeBack({ kitegateway_reference: record.kitegateway_reference }));
    setTimeout(() => {
      handleModalCancelTransactionModal();
    }, 2000);
  };

  const handleCheckTransactionStatus = (serviceType, kitegatewayReference) => {
    dispatch(checkTransactionStatus(serviceType, kitegatewayReference));
  };

  const handleTriggerWebhook = (kitegatewayReference) => {
    dispatch(triggerWebhook(kitegatewayReference));
  };

  const handleUpdateTransactionStatus = () => {
    setVisibilityTransactionModal(false);
    if (selectedTransaction.transaction_type === TransactionTypes.REFUND) {
      if (formEditRefund.current) {
        formEditRefund.current.resetFields();
      }
      setVisibleUpdateRefundStatusModal(true);
      setTimeout(() => {
        setVisibleUpdateRefundStatusModal(false);
        if (formEditRefund.current) {
          formEditRefund.current.resetFields();
        }
        setTimeout(() => {
          setVisibleUpdateRefundStatusModal(true);
        }, 1);
      }, 1);
    } else if (selectedTransaction.transaction_type === TransactionTypes.SETTLEMENT) {
      if (formEditSettlement.current) {
        formEditSettlement.current.resetFields();
      }
      setVisibleUpdateSettlmtStatusModal(true);
      setTimeout(() => {
        setVisibleUpdateSettlmtStatusModal(false);
        if (formEditSettlement.current) {
          formEditSettlement.current.resetFields();
        }
        setTimeout(() => {
          setVisibleUpdateSettlmtStatusModal(true);
        }, 1);
      }, 1);
    } else if (selectedTransaction.transaction_type === TransactionTypes.CHARGEBACK) {
      if (formEditChargeback.current) {
        formEditChargeback.current.resetFields();
      }
      setVisibleUpdateChargebkStatusModal(true);
      setTimeout(() => {
        setVisibleUpdateChargebkStatusModal(false);
        if (formEditChargeback.current) {
          formEditChargeback.current.resetFields();
        }
        setTimeout(() => {
          setVisibleUpdateChargebkStatusModal(true);
        }, 1);
      }, 1);
    }
  };

  const handleRefundTransaction = () => {
    setVisibilityTransactionModal(false);
    if (formCreateRefund.current) {
      formCreateRefund.current.resetFields();
    }
    setVisibleCreateRefundModal(true);
    setTimeout(() => {
      setVisibleCreateRefundModal(false);
      if (formCreateRefund.current) {
        formCreateRefund.current.resetFields();
      }
      setTimeout(() => {
        setVisibleCreateRefundModal(true);
      }, 1);
    }, 1);
  };

  const handleModalCancelUpdateStatusModal = () => {
    setVisibleUpdateRefundStatusModal(false);
    setVisibleUpdateSettlmtStatusModal(false);
    setVisibleUpdateChargebkStatusModal(false);
    setSelectedTransaction({});
  };

  const handleModalCancelCreateRefundModal = () => {
    setVisibleCreateRefundModal(false);
    handleModalCancelUpdateStatusModal();
  };

  const handleStatusChange = (value) => {
    setNewStatus(value);
  };

  const handleOnClearTransactionType = () => {
    setSelectedTransactionType('All');
    // Use the locally selected status, not the prop
    const currentStatus = selectedTransactionStatus === 'All' ? '' : selectedTransactionStatus.toUpperCase();
    const currentMidBank = selectedMidBank === 'All' ? '' : selectedMidBank;
    const _transactionFilter = generateTransactionsFilter(merchantId, transactionStatus || currentStatus, "", currentMidBank);
    handleSearch(searchValue, undefined, _transactionFilter);
  };

  const handleOnClearTransactionStatus = () => {
    setSelectedTransactionStatus('All');
    // Use the locally selected type, not the prop
    const currentType = selectedTransactionType === 'All' ? '' : selectedTransactionType.toUpperCase();
    const currentMidBank = selectedMidBank === 'All' ? '' : selectedMidBank;
    const _transactionFilter = generateTransactionsFilter(merchantId, "", transactionType || currentType, currentMidBank);
    handleSearch(searchValue, undefined, _transactionFilter);
  };

  const handleOnClearMidBank = () => {
    setSelectedMidBank('All');
    const currentStatus = selectedTransactionStatus === 'All' ? '' : selectedTransactionStatus.toUpperCase();
    const currentType = selectedTransactionType === 'All' ? '' : selectedTransactionType.toUpperCase();
    const _transactionFilter = generateTransactionsFilter(merchantId, transactionStatus || currentStatus, transactionType || currentType, "");
    handleSearch(searchValue, undefined, _transactionFilter);
  };

  const handleTransactionTypeChange = (value) => {
    const newValue = value ?? "All";
    setSelectedTransactionType(newValue);
    // Use the locally selected status, not the prop
    const currentStatus = selectedTransactionStatus === 'All' ? '' : selectedTransactionStatus.toUpperCase();
    const currentMidBank = selectedMidBank === 'All' ? '' : selectedMidBank;
    const _transactionFilter = generateTransactionsFilter(
      merchantId,
      transactionStatus || currentStatus,
      ['ALL', 'All'].includes(newValue) ? "" : newValue.toUpperCase(),
      currentMidBank
    );
    handleSearch(searchValue, undefined, _transactionFilter);
  };

  const handleTransactionStatusChange = (value) => {
    const newValue = value ?? "All";
    setSelectedTransactionStatus(newValue);
    // Use the locally selected type, not the prop
    const currentType = selectedTransactionType === 'All' ? '' : selectedTransactionType.toUpperCase();
    const currentMidBank = selectedMidBank === 'All' ? '' : selectedMidBank;
    const _transactionFilter = generateTransactionsFilter(
      merchantId,
      ['ALL', 'All'].includes(newValue) ? "" : newValue.toUpperCase(),
      transactionType || currentType,
      currentMidBank
    );
    handleSearch(searchValue, undefined, _transactionFilter);
  };

  const handleMidBankChange = (value) => {
    const newValue = value ?? "All";
    setSelectedMidBank(newValue);
    const currentStatus = selectedTransactionStatus === 'All' ? '' : selectedTransactionStatus.toUpperCase();
    const currentType = selectedTransactionType === 'All' ? '' : selectedTransactionType.toUpperCase();
    const _transactionFilter = generateTransactionsFilter(
      merchantId,
      transactionStatus || currentStatus,
      transactionType || currentType,
      ['ALL', 'All'].includes(newValue) ? "" : newValue
    );
    handleSearch(searchValue, undefined, _transactionFilter);
  };

  const handleModalOkUpdateRefundStatusModal = () => {
    if (formEditRefund.current) {
      formEditRefund.current.validateFields().then((value) => {
        if (!newStatus) return;
        if (newStatus === 'COMPLETED' && !value.processor_reference && !value.refund_id) return;

        dispatch(updateRefundStatus({
          ...value,
          transaction_status: newStatus,
        }));
        setTimeout(() => {
          handleModalCancelUpdateStatusModal();
        }, 2000);
      }).catch((error) => {
        console.error(error.errorFields);
      });
    }
  };

  const handleModalOkUpdateSettlementStatusModal = () => {
    if (formEditSettlement.current) {
      formEditSettlement.current.validateFields().then((value) => {
        if (!newStatus) return;

        dispatch(updateSettlementStatus({
          ...value,
          transaction_status: newStatus,
        }));
        setTimeout(() => {
          handleModalCancelUpdateStatusModal();
        }, 2000);
      }).catch((error) => {
        console.error(error.errorFields);
      });
    }
  };

  const handleModalOkUpdateChargebackStatusModal = () => {
    if (formEditChargeback.current) {
      formEditChargeback.current.validateFields().then((value) => {
        if (!newStatus) return;

        dispatch(updateChargebackStatus({
          ...value,
          transaction_status: newStatus,
        }));
        setTimeout(() => {
          handleModalCancelUpdateStatusModal();
        }, 2000);
      }).catch((error) => {
        console.error(error.errorFields);
      });
    }
  };

  const handleModalOkCreateRefundModal = () => {
    if (formCreateRefund.current) {
      formCreateRefund.current.validateFields().then((value) => {
        dispatch(createRefund({
          ...value,
          amount: Number(value.amount)
        }));
        setTimeout(() => {
          handleModalCancelCreateRefundModal();
        }, 2000);
      }).catch((error) => {
        console.error(error.errorFields);
      });
    }
  };

  // Helper function to format payment method with masked card number
  const formatPaymentMethod = (transaction) => {
    const cardType = transaction.card_type || 'Card';
    const accountNumber = transaction.account_number;

    // If we have an account number with at least 4 digits, format it
    if (accountNumber && accountNumber.length >= 4) {
      const lastFour = accountNumber.slice(-4);
      return `${cardType} ••••${lastFour}`;
    }

    // Otherwise just return the card type
    return cardType;
  };  // PDF Generation Functions
  const generateInvoicePDF = (transaction, customData = null) => {
    const doc = new JsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;

    // Use customData if provided, otherwise use original transaction data
    const companyName = customData?.company?.name || 'KiteGateway';
    const companyAddress = customData?.company?.address || 'Payment Gateway Services';
    const companyEmail = customData?.company?.email || 'support@kitegateway.com';

    const billToName = customData?.billTo?.name || transaction.customer_name || transaction.business_name || transaction.billing?.name || 'Customer';
    const billToAddress = customData?.billTo?.address || transaction.billing?.address || transaction.customer_address || '';
    const billToEmail = customData?.billTo?.email || transaction.customer_email || transaction.billing?.email || transaction.email || '';
    const billToPhone = customData?.billTo?.phone || transaction.customer_phone || transaction.customer_mobile || transaction.billing?.phone || transaction.phone_number || '';

    const itemDescription = customData?.item?.description || transaction.narration || `${transaction.transaction_type} Transaction`;
    const itemAmount = customData?.item?.amount || transaction.transaction_amount;
    const itemCurrency = customData?.item?.currency || transaction.transaction_currency;
    const merchantRef = customData?.item?.merchantReference || transaction.merchant_reference;
    const notes = customData?.notes || '';

    // Add logo
    try {
      doc.addImage(logo, 'PNG', 15, 10, 30, 12);
    } catch (e) {
      console.log('Logo not loaded');
    }

    // Header
    doc.setFontSize(20);
    doc.setFont(undefined, 'bold');
    doc.text('INVOICE', pageWidth - 15, 20, { align: 'right' });

    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(companyName, pageWidth - 15, 27, { align: 'right' });
    doc.text(companyAddress, pageWidth - 15, 32, { align: 'right' });
    if (companyEmail) {
      doc.text(companyEmail, pageWidth - 15, 37, { align: 'right' });
    }

    // Invoice details
    doc.setFontSize(11);
    doc.setFont(undefined, 'bold');
    doc.text('Invoice Details', 15, 45);

    doc.setFont(undefined, 'normal');
    doc.setFontSize(10);
    const invoiceDetails = [
      ['Invoice Date:', moment(transaction.created_at).utc().add('hours', timezone.offset).format('DD/MM/YYYY h:mm:ss a')],
      ['Invoice Number:', transaction.kitegateway_reference],
      ['Transaction Type:', transaction.transaction_type],
      ['Status:', transaction.transaction_status],
      ['Payment Method:', formatPaymentMethod(transaction)],
    ];

    if (merchantRef) {
      invoiceDetails.push(['Merchant Ref:', merchantRef]);
    }    let yPos = 53;
    invoiceDetails.forEach(([label, value]) => {
      doc.text(label, 15, yPos);
      doc.text(value || 'N/A', 60, yPos);
      yPos += 6;
    });

    // Bill To
    doc.setFont(undefined, 'bold');
    doc.text('Bill To:', 15, yPos + 5);
    doc.setFont(undefined, 'normal');
    yPos += 11;
    doc.text(billToName, 15, yPos);

    if (billToAddress) {
      yPos += 5;
      const addressLines = doc.splitTextToSize(billToAddress, 80);
      doc.text(addressLines, 15, yPos);
      yPos += (addressLines.length * 5);
    }

    if (billToEmail) {
      yPos += 5;
      doc.text(`Email: ${billToEmail}`, 15, yPos);
    }

    if (billToPhone) {
      yPos += 5;
      doc.text(`Phone: ${billToPhone}`, 15, yPos);
    }

    // Transaction details table
    yPos += 15;
    doc.autoTable({
      startY: yPos,
      head: [['Description', 'Amount', 'Currency']],
      body: [
        [
          itemDescription,
          new Intl.NumberFormat().format(itemAmount),
          itemCurrency
        ]
      ],
      theme: 'striped',
      headStyles: { fillColor: [41, 128, 185] },
      margin: { left: 15, right: 15 },
    });

    // Total
    const finalY = doc.lastAutoTable.finalY + 10;
    doc.setFont(undefined, 'bold');
    doc.setFontSize(12);
    doc.text('Total Amount:', pageWidth - 80, finalY);
    doc.text(`${itemCurrency} ${new Intl.NumberFormat().format(itemAmount)}`, pageWidth - 15, finalY, { align: 'right' });

    // Notes if provided
    if (notes) {
      doc.setFontSize(10);
      doc.setFont(undefined, 'bold');
      doc.text('Notes:', 15, finalY + 15);
      doc.setFont(undefined, 'normal');
      const notesLines = doc.splitTextToSize(notes, pageWidth - 30);
      doc.text(notesLines, 15, finalY + 21);
    }

    // Footer
    doc.setFontSize(9);
    doc.setFont(undefined, 'italic');
    doc.text('Thank you for your business!', pageWidth / 2, pageHeight - 20, { align: 'center' });
    doc.text(`Processor: ${transaction.processor?.name || 'N/A'}`, pageWidth / 2, pageHeight - 15, { align: 'center' });

    // Save
    doc.save(`Invoice-${transaction.kitegateway_reference}.pdf`);
  };

  const generateReceiptPDF = (transaction, customData = null) => {
    const doc = new JsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;

    // Use customData if provided, otherwise use original transaction data
    const companyName = customData?.company?.name || 'KiteGateway';
    const companyAddress = customData?.company?.address || 'Payment Gateway Services';
    const companyEmail = customData?.company?.email || 'support@kitegateway.com';

    const billToName = customData?.billTo?.name || transaction.customer_name || transaction.business_name || transaction.billing?.name || 'Customer';
    const billToAddress = customData?.billTo?.address || transaction.billing?.address || transaction.customer_address || '';
    const billToEmail = customData?.billTo?.email || transaction.customer_email || transaction.billing?.email || transaction.email || '';
    const billToPhone = customData?.billTo?.phone || transaction.customer_phone || transaction.customer_mobile || transaction.billing?.phone || transaction.phone_number || '';

    const itemDescription = customData?.item?.description || transaction.narration || `${transaction.transaction_type} Payment`;
    const itemAmount = customData?.item?.amount || transaction.transaction_amount;
    const itemCurrency = customData?.item?.currency || transaction.transaction_currency;
    const merchantRef = customData?.item?.merchantReference || transaction.merchant_reference;
    const notes = customData?.notes || '';

    // Add logo
    try {
      doc.addImage(logo, 'PNG', 15, 10, 30, 12);
    } catch (e) {
      console.log('Logo not loaded');
    }

    // Header
    doc.setFontSize(20);
    doc.setFont(undefined, 'bold');
    doc.text('RECEIPT', pageWidth - 15, 20, { align: 'right' });

    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(companyName, pageWidth - 15, 27, { align: 'right' });
    doc.text(companyAddress, pageWidth - 15, 32, { align: 'right' });
    if (companyEmail) {
      doc.text(companyEmail, pageWidth - 15, 37, { align: 'right' });
    }

    // Receipt details
    doc.setFontSize(11);
    doc.setFont(undefined, 'bold');
    doc.text('Receipt Details', 15, 45);

    doc.setFont(undefined, 'normal');
    doc.setFontSize(10);
    const receiptDetails = [
      ['Receipt Date:', moment(transaction.created_at).utc().add('hours', timezone.offset).format('DD/MM/YYYY h:mm:ss a')],
      ['Receipt Number:', transaction.kitegateway_reference],
      ['Transaction Type:', transaction.transaction_type],
      ['Status:', transaction.transaction_status],
      ['Processor Reference:', transaction.processor_reference || 'N/A'],
    ];

    if (merchantRef) {
      receiptDetails.push(['Merchant Ref:', merchantRef]);
    }

    let yPos = 53;
    receiptDetails.forEach(([label, value]) => {
      doc.text(label, 15, yPos);
      doc.text(value || 'N/A', 65, yPos);
      yPos += 6;
    });

    // Received From
    doc.setFont(undefined, 'bold');
    doc.text('Received From:', 15, yPos + 5);
    doc.setFont(undefined, 'normal');
    yPos += 11;
    doc.text(billToName, 15, yPos);

    if (billToAddress) {
      yPos += 5;
      const addressLines = doc.splitTextToSize(billToAddress, 80);
      doc.text(addressLines, 15, yPos);
      yPos += (addressLines.length * 5);
    }

    if (billToEmail) {
      yPos += 5;
      doc.text(`Email: ${billToEmail}`, 15, yPos);
    }

    if (billToPhone) {
      yPos += 5;
      doc.text(`Phone: ${billToPhone}`, 15, yPos);
    }

    // Payment details
    yPos += 15;
    doc.autoTable({
      startY: yPos,
      head: [['Description', 'Payment Method', 'Amount']],
      body: [
        [
          itemDescription,
          formatPaymentMethod(transaction),
          `${itemCurrency} ${new Intl.NumberFormat().format(itemAmount)}`
        ]
      ],
      theme: 'striped',
      headStyles: { fillColor: [39, 174, 96] },
      margin: { left: 15, right: 15 },
    });

    // Total Received
    const finalY = doc.lastAutoTable.finalY + 10;
    doc.setFont(undefined, 'bold');
    doc.setFontSize(12);
    doc.text('Total Received:', pageWidth - 80, finalY);
    doc.text(`${itemCurrency} ${new Intl.NumberFormat().format(itemAmount)}`, pageWidth - 15, finalY, { align: 'right' });

    // Notes if provided
    if (notes) {
      doc.setFontSize(10);
      doc.setFont(undefined, 'bold');
      doc.text('Notes:', 15, finalY + 15);
      doc.setFont(undefined, 'normal');
      const notesLines = doc.splitTextToSize(notes, pageWidth - 30);
      doc.text(notesLines, 15, finalY + 21);
    }

    // Footer
    doc.setFontSize(9);
    doc.setFont(undefined, 'italic');
    doc.text('This is an official receipt for your payment', pageWidth / 2, pageHeight - 20, { align: 'center' });
    doc.text(`Reference: ${transaction.kitegateway_reference}`, pageWidth / 2, pageHeight - 15, { align: 'center' });

    // Save
    doc.save(`Receipt-${transaction.kitegateway_reference}.pdf`);
  };

  const generateInvoiceAndReceiptPDF = (transaction, customData = null) => {
    const doc = new JsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;

    // Use customData if provided, otherwise use original transaction data
    const companyName = customData?.company?.name || 'KiteGateway';
    const companyAddress = customData?.company?.address || 'Payment Gateway Services';
    const companyEmail = customData?.company?.email || 'support@kitegateway.com';

    const billToName = customData?.billTo?.name || transaction.customer_name || transaction.business_name || transaction.billing?.name || 'Customer';
    const billToAddress = customData?.billTo?.address || transaction.billing?.address || transaction.customer_address || '';
    const billToEmail = customData?.billTo?.email || transaction.customer_email || transaction.billing?.email || transaction.email || '';
    const billToPhone = customData?.billTo?.phone || transaction.customer_phone || transaction.customer_mobile || transaction.billing?.phone || transaction.phone_number || '';

    const itemDescription = customData?.item?.description || transaction.narration || `${transaction.transaction_type} Transaction`;
    const itemAmount = customData?.item?.amount || transaction.transaction_amount;
    const itemCurrency = customData?.item?.currency || transaction.transaction_currency;
    const merchantRef = customData?.item?.merchantReference || transaction.merchant_reference;
    const notes = customData?.notes || '';

    // PAGE 1 - INVOICE
    try {
      doc.addImage(logo, 'PNG', 15, 10, 30, 12);
    } catch (e) {
      console.log('Logo not loaded');
    }

    doc.setFontSize(20);
    doc.setFont(undefined, 'bold');
    doc.text('INVOICE', pageWidth - 15, 20, { align: 'right' });

    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(companyName, pageWidth - 15, 27, { align: 'right' });
    doc.text(companyAddress, pageWidth - 15, 32, { align: 'right' });
    if (companyEmail) {
      doc.text(companyEmail, pageWidth - 15, 37, { align: 'right' });
    }

    doc.setFontSize(11);
    doc.setFont(undefined, 'bold');
    doc.text('Invoice Details', 15, 45);

    doc.setFont(undefined, 'normal');
    doc.setFontSize(10);
    let yPos = 53;
    const invoiceDetails = [
      ['Invoice Date:', moment(transaction.created_at).utc().add('hours', timezone.offset).format('DD/MM/YYYY h:mm:ss a')],
      ['Invoice Number:', transaction.kitegateway_reference],
      ['Transaction Type:', transaction.transaction_type],
      ['Payment Method:', formatPaymentMethod(transaction)],
    ];

    if (merchantRef) {
      invoiceDetails.push(['Merchant Ref:', merchantRef]);
    }

    invoiceDetails.forEach(([label, value]) => {
      doc.text(label, 15, yPos);
      doc.text(value || 'N/A', 60, yPos);
      yPos += 6;
    });

    doc.setFont(undefined, 'bold');
    doc.text('Bill To:', 15, yPos + 5);
    doc.setFont(undefined, 'normal');
    yPos += 11;
    doc.text(billToName, 15, yPos);

    if (billToAddress) {
      yPos += 5;
      const addressLines = doc.splitTextToSize(billToAddress, 80);
      doc.text(addressLines, 15, yPos);
      yPos += (addressLines.length * 5);
    }

    if (billToEmail) {
      yPos += 5;
      doc.text(`Email: ${billToEmail}`, 15, yPos);
    }

    yPos += 15;
    doc.autoTable({
      startY: yPos,
      head: [['Description', 'Amount', 'Currency']],
      body: [
        [
          itemDescription,
          new Intl.NumberFormat().format(itemAmount),
          itemCurrency
        ]
      ],
      theme: 'striped',
      headStyles: { fillColor: [41, 128, 185] },
      margin: { left: 15, right: 15 },
    });

    let finalY = doc.lastAutoTable.finalY + 10;
    doc.setFont(undefined, 'bold');
    doc.setFontSize(12);
    doc.text('Total Amount:', pageWidth - 80, finalY);
    doc.text(`${itemCurrency} ${new Intl.NumberFormat().format(itemAmount)}`, pageWidth - 15, finalY, { align: 'right' });

    if (notes) {
      doc.setFontSize(10);
      doc.setFont(undefined, 'bold');
      doc.text('Notes:', 15, finalY + 15);
      doc.setFont(undefined, 'normal');
      const notesLines = doc.splitTextToSize(notes, pageWidth - 30);
      doc.text(notesLines, 15, finalY + 21);
    }

    // PAGE 2 - RECEIPT
    doc.addPage();

    try {
      doc.addImage(logo, 'PNG', 15, 10, 30, 12);
    } catch (e) {
      console.log('Logo not loaded');
    }

    doc.setFontSize(20);
    doc.setFont(undefined, 'bold');
    doc.text('RECEIPT', pageWidth - 15, 20, { align: 'right' });

    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(companyName, pageWidth - 15, 27, { align: 'right' });
    doc.text(companyAddress, pageWidth - 15, 32, { align: 'right' });
    if (companyEmail) {
      doc.text(companyEmail, pageWidth - 15, 37, { align: 'right' });
    }

    doc.setFontSize(11);
    doc.setFont(undefined, 'bold');
    doc.text('Receipt Details', 15, 45);

    doc.setFont(undefined, 'normal');
    doc.setFontSize(10);
    yPos = 53;
    const receiptDetails = [
      ['Receipt Date:', moment(transaction.created_at).utc().add('hours', timezone.offset).format('DD/MM/YYYY h:mm:ss a')],
      ['Receipt Number:', transaction.kitegateway_reference],
      ['Status:', transaction.transaction_status],
    ];

    if (merchantRef) {
      receiptDetails.push(['Merchant Ref:', merchantRef]);
    }

    receiptDetails.forEach(([label, value]) => {
      doc.text(label, 15, yPos);
      doc.text(value || 'N/A', 65, yPos);
      yPos += 6;
    });

    doc.setFont(undefined, 'bold');
    doc.text('Received From:', 15, yPos + 5);
    doc.setFont(undefined, 'normal');
    yPos += 11;
    doc.text(billToName, 15, yPos);

    if (billToAddress) {
      yPos += 5;
      const addressLines = doc.splitTextToSize(billToAddress, 80);
      doc.text(addressLines, 15, yPos);
      yPos += (addressLines.length * 5);
    }

    if (billToEmail) {
      yPos += 5;
      doc.text(`Email: ${billToEmail}`, 15, yPos);
    }

    yPos += 15;
    doc.autoTable({
      startY: yPos,
      head: [['Description', 'Payment Method', 'Amount']],
      body: [
        [
          itemDescription,
          formatPaymentMethod(transaction),
          `${itemCurrency} ${new Intl.NumberFormat().format(itemAmount)}`
        ]
      ],
      theme: 'striped',
      headStyles: { fillColor: [39, 174, 96] },
      margin: { left: 15, right: 15 },
    });

    finalY = doc.lastAutoTable.finalY + 10;
    doc.setFont(undefined, 'bold');
    doc.setFontSize(12);
    doc.text('Total Received:', pageWidth - 80, finalY);
    doc.text(`${itemCurrency} ${new Intl.NumberFormat().format(itemAmount)}`, pageWidth - 15, finalY, { align: 'right' });

    if (notes) {
      doc.setFontSize(10);
      doc.setFont(undefined, 'bold');
      doc.text('Notes:', 15, finalY + 15);
      doc.setFont(undefined, 'normal');
      const notesLines = doc.splitTextToSize(notes, pageWidth - 30);
      doc.text(notesLines, 15, finalY + 21);
    }

    // Save
    doc.save(`Invoice-Receipt-${transaction.kitegateway_reference}.pdf`);
  };

  const handleMenuClick = (record, action) => {
    // Open edit modal instead of directly generating PDF
    setEditModalTransaction(record);
    setEditModalDocumentType(action);
    setVisibleEditModal(true);
  };

  const handleEditModalGenerate = (customData) => {
    // Generate PDF with customized data
    const transaction = editModalTransaction;

    switch (editModalDocumentType) {
      case 'invoice':
        generateInvoicePDF(transaction, customData);
        break;
      case 'receipt':
        generateReceiptPDF(transaction, customData);
        break;
      case 'both':
        generateInvoiceAndReceiptPDF(transaction, customData);
        break;
      default:
        break;
    }

    // Close modal
    setVisibleEditModal(false);
    setEditModalTransaction(null);
  };

  const handleEditModalCancel = () => {
    setVisibleEditModal(false);
    setEditModalTransaction(null);
  };

  const getActionsMenu = (record) => (
    <Menu onClick={({ key }) => handleMenuClick(record, key)}>
      <Menu.Item key="invoice" icon={<FilePdfOutlined />}>
        Generate Invoice
      </Menu.Item>
      <Menu.Item key="receipt" icon={<FilePdfOutlined />}>
        Generate Receipt
      </Menu.Item>
      <Menu.Item key="both" icon={<FilePdfOutlined />}>
        Generate Invoice & Receipt
      </Menu.Item>
    </Menu>
  );

  // Check if user can generate invoices/receipts (Super Admin or Admin only)
  const canGenerateInvoiceReceipt = userRole === 'Super Admin' || userRole === 'Admin';

  const props = {
    loading,
    pagination: false,
    rowKey: '_id',
    columns: transactionColumns(processorsData, timezone, canGenerateInvoiceReceipt ? (record) => (
      <Dropdown overlay={getActionsMenu(record)} trigger={['click']}>
        <Button
          type="text"
          icon={<MoreOutlined />}
          onClick={(e) => e.stopPropagation()}
        />
      </Dropdown>
    ) : null),
    dataSource: allTransactions.map((item) => ({
      ...item,
      processor: {
        _id: item.processor,
        name: item.processor?.name || processorsData.find((element) => element._id === item.processor)?.name,
        service_type: item.processor?.service_type || processorsData.find((element) => element._id === item.processor)?.service_type,
      },
    })),
    onChange: handleOnChange,
    onRow: viewTransactionDetails,
  };

  return (
    <div>
      <div className="justify-content-center">
        <Row>
          <Col xl={24} lg={24} md={24} sm={24} xs={24}>
            <DateRange />
          </Col>
        </Row>
        <Row gutter={[16, 16]} align="bottom">
          {merchantId && (
            <Col xl={4} lg={5} md={8} sm={12} xs={24}>
              <GeneratePRN merchantId={merchantId} />
            </Col>
          )}
          {!transactionType && (
            <Col xl={4} lg={5} md={8} sm={12} xs={24}>
              <Form name="basic" className="gx-form-row0">
                <label className="gx-mb-1 gx-d-block">Transaction Type</label>
                <Select
                  allowClear
                  style={{ width: '100%' }}
                  value={selectedTransactionType}
                  onClear={handleOnClearTransactionType}
                  placeholder="Select Transaction Type"
                  onChange={handleTransactionTypeChange}
                >
                  <Select.Option value="ALL">All</Select.Option>
                  {Object.values(TransactionTypes).map((type) => (
                    <Select.Option key={type} value={type}>
                      {capitalize(type)}
                    </Select.Option>
                  ))}
                </Select>
              </Form>
            </Col>
          )}
          {!transactionStatus && (
            <Col xl={4} lg={5} md={8} sm={12} xs={24}>
              <Form name="basic" className="gx-form-row0">
                <label className="gx-mb-1 gx-d-block">Transaction Status</label>
                <Select
                  allowClear
                  style={{ width: '100%' }}
                  value={selectedTransactionStatus}
                  onClear={handleOnClearTransactionStatus}
                  placeholder="Select Transaction Status"
                  onChange={handleTransactionStatusChange}
                >
                  <Select.Option value="ALL">All</Select.Option>
                  {Object.values(TransactionStatuses).map((status) => (
                    <Select.Option key={status} value={status}>
                      {capitalize(status)}
                    </Select.Option>
                  ))}
                </Select>
              </Form>
            </Col>
          )}
          {canFilterByMidBank && (
            <Col xl={4} lg={5} md={8} sm={12} xs={24}>
              <Form name="basic" className="gx-form-row0">
                <label className="gx-mb-1 gx-d-block">Mid Bank</label>
                <Select
                  allowClear
                  style={{ width: '100%' }}
                  value={selectedMidBank}
                  onClear={handleOnClearMidBank}
                  placeholder="Select Mid Bank"
                  onChange={handleMidBankChange}
                >
                  <Select.Option value="ALL">All</Select.Option>
                  {MidBanks.map((midBank) => (
                    <Select.Option key={midBank} value={midBank}>
                      {midBank}
                    </Select.Option>
                  ))}
                </Select>
              </Form>
            </Col>
          )}
        </Row>
        <Row gutter={[16, 16]} align="middle" className="gx-mt-2">
          <Col flex="auto">
            <Input.Search
              placeholder="Search transactions"
              enterButton
              onSearch={handleSearch}
            />
          </Col>
          <Col flex="none">
            <DownloadBtns
              handleXclRefunds={[null, TransactionTypes.REFUND].includes(transactionType) ? handleXclRefunds : null}
              handlePdf={() => openDownloadModal('pdf')}
              handleXcl={() => openDownloadModal('excel')}
              handleSummary={() => handleDownloadSummary()}
              downloading={downloadLoading}
            />
          </Col>
        </Row>
        <Table className="gx-table-responsive" {...props} />
        <Row className="pagination">
          <Col md={12}>
            <PageLimitSwitch
              resourceType="allTransactions"
              limit={pageSize}
              limits={[10, 100, 200, 500, 1000, 2000, 5000, 10000]}
              totalRecords={totalRecords}
              currentPage={currentPage}
            />
          </Col>
        </Row>
        <InfoView />
      </div>
      <Modal
        visible={visibleTransactionModal}
        okText="Close"
        confirmLoading={loading}
        onCancel={handleModalCancelTransactionModal}
        onOk={handleModalCancelTransactionModal}
        footer={selectedTransaction.kitegateway_reference ? `P/R: ${selectedTransaction.kitegateway_reference}` : null}
      >
        {selectedTransaction?.transaction_type === TransactionTypes.COLLECTION &&
          selectedTransaction?.transaction_status === TransactionStatuses.COMPLETED && (
            <Can I="create" a={TRANSACTION_CHARGEBACKS}>
              <Popconfirm
                title={`Are you sure? ${selectedTransaction.transaction_amount} ${selectedTransaction.transaction_currency?.code} will be debited.`}
                okText="Yes"
                cancelText="No"
                onConfirm={() => chargeBack(selectedTransaction)}
              >
                <Button type="primary" className="ant-btn-sm ant-btn-background-ghost">
                  Add Charge back
                </Button>
              </Popconfirm>
            </Can>
          )}
        <TransactionDetailsForModal
          selectedTransaction={selectedTransaction}
          handleUpdateTransactionStatus={handleUpdateTransactionStatus}
          handleRefundTransaction={handleRefundTransaction}
          handleCheckTransactionStatus={handleCheckTransactionStatus}
          handleTriggerWebhook={handleTriggerWebhook}
        />
      </Modal>
      {selectedTransaction?.kitegateway_reference && (
        <Modal
          visible={visibleUpdateRefundStatusModal}
          confirmLoading={loading}
          onCancel={handleModalCancelUpdateStatusModal}
          onOk={handleModalOkUpdateRefundStatusModal}
          okText="Update"
        >
          <RefundUpdateForm
            form={formEditRefund}
            handleStatusChange={handleStatusChange}
            kitegatewayReference={selectedTransaction.kitegateway_reference}
          />
        </Modal>
      )}
      {selectedTransaction && (
        <Modal
          visible={visibleUpdateSettlmtStatusModal}
          confirmLoading={loading}
          onCancel={handleModalCancelUpdateStatusModal}
          onOk={handleModalOkUpdateSettlementStatusModal}
          okText="Update"
        >
          <TransactionSettlementForm
            form={formEditSettlement}
            handleStatusChange={handleStatusChange}
            selectedStatus={newStatus}
            selectedTransaction={selectedTransaction}
          />
        </Modal>
      )}
      {selectedTransaction && (
        <Modal
          visible={visibleUpdateChargebackStatusModal}
          confirmLoading={loading}
          onCancel={handleModalCancelUpdateStatusModal}
          onOk={handleModalOkUpdateChargebackStatusModal}
          okText="Update"
        >
          <TransactionChargebackForm
            form={formEditChargeback}
            handleStatusChange={handleStatusChange}
            selectedStatus={newStatus}
            selectedTransaction={selectedTransaction}
            kitegatewayReference={selectedTransaction.kitegateway_reference}
          />
        </Modal>
      )}
      {selectedTransaction && (
        <Modal
          visible={visibleCreateRefundModal}
          confirmLoading={loading}
          onCancel={handleModalCancelCreateRefundModal}
          onOk={handleModalOkCreateRefundModal}
          okText="Refund"
        >
          <TransactionCreateRefundForm
            form={formCreateRefund}
            selectedTransaction={selectedTransaction}
            kitegatewayReference={selectedTransaction.kitegateway_reference}
          />
        </Modal>
      )}

      {/* Invoice/Receipt Edit Modal */}
      <InvoiceReceiptEditModal
        visible={visibleEditModal}
        onCancel={handleEditModalCancel}
        onGenerate={handleEditModalGenerate}
        transaction={editModalTransaction}
        documentType={editModalDocumentType}
        timezone={timezone}
      />
    </div>
  );
};

Transactions.propTypes = {
  merchantId: PropTypes.string,
  transactionType: PropTypes.string,
  transactionStatus: PropTypes.string,
};

Transactions.defaultProps = {
  merchantId: null,
  transactionType: null,
  transactionStatus: null,
};

export default Transactions;
